import React, { useCallback, memo, useState, useContext } from 'react';
import AddNewObjectButton from '../../AddNewObjectButton/AddNewObjectButton';
import UrvalButton, {
  IUrvalButtonProps,
} from '../../../../../../Components/Urval/Components/UrvalButton/UrvalButton';
import KolumnValjareDialog from '../../../../../../Components/KolumnValjare/Components/KolumnValjareDialog/KolumnValjareDialog';
import { useBoolState } from '../../../../../../Hooks/booleanHooks';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';
import EDMenu from '../../../../../../BaseComponents/Menu/EDMenu';
import EDMenuItem from '../../../../../../BaseComponents/Menu/EDMenuItem';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { RutinSettingsContext } from '../../RutinAvailabilityContext/RutinAvailabilityContext';
import {
  findBusinessColors,
  findBusinessMenuUnusedButtons,
} from '../../../Const/BusinessConfigs';
import { CollectionMenuDisabledReason } from './BusinessCollectionMenu';
import {
  findRoutineMenuCustomAddButton,
  findRoutineMenuCustomUrvalButton,
  findRoutineMenuUnusedButtons,
} from '../../../Const/Rutiner';
import { IRenderButtonProps } from '../../../Const/Delsystem';
import type { IProps } from './BusinessCollectionMenu';
import LegendMenuButton from '../../../../../../Components/Legend/LegendMenuButton';
import { useExportToExcel } from '../../../../../../Components/ExportToExcel/ExportToExcel';
import FloatingMenu, {
  FloatingMenuButton,
  FloatingMenuButtonLabel,
} from '../../../../../../Components/FloatingBottomArea/FloatingMenu';

const emptyUrval = { pages: [] };

const menuTextStyle = { marginLeft: '16px' } as const;
const selectedMenuTextStyle = {
  marginLeft: '16px',
  fontWeight: 'bold',
} as const;

/**
 * Floating glass bottom menu for business collections on touch devices.
 * Self-positioned fixed element at z-index below EDNavigationOverlay so
 * the detail overlay naturally covers this menu when it opens.
 */
const FloatingBusinessCollectionMenu = (props: IProps) => {
  const { refetch } = props;
  const [open, onOpen, onClose] = useBoolState(false);
  const [tabValue, setTabValue] = useState(0);
  const [miscMenuAnchor, setMiscMenuAnchor] = useState<HTMLElement | undefined>(
    undefined
  );
  const handleRefetch = useCallback(() => {
    refetch();
    setMiscMenuAnchor(undefined);
  }, [refetch]);
  const rutinSettingsContext = useContext(RutinSettingsContext);

  const exportToExcel = useExportToExcel({
    collectionName: props.collection.CollectionName,
    columns: props.columns,
    sortProperties: props.sortedData.properties,
    urval: props.urval,
    searchText: props.searchText,
    rows: props.rows,
  });

  const setMenuAnchor = (event: React.MouseEvent<HTMLElement>) => {
    setMiscMenuAnchor(event.currentTarget);
  };

  const businessColors = findBusinessColors(props.collection.CollectionName);

  let unusedButtons = undefined;
  if (props.collection.CollectionName === props.baseCollection.CollectionName)
    unusedButtons = findRoutineMenuUnusedButtons(
      rutinSettingsContext.activeRoutine?.routine.rutinKod
    );
  if (!unusedButtons)
    unusedButtons =
      findBusinessMenuUnusedButtons(props.collection.CollectionName) ?? [];

  let UseUrvalButton:
    | React.FC<React.PropsWithChildren<IUrvalButtonProps>>
    | undefined = undefined;
  if (props.collection.CollectionName === props.baseCollection.CollectionName)
    UseUrvalButton = findRoutineMenuCustomUrvalButton(
      rutinSettingsContext.activeRoutine?.routine.rutinKod
    );
  if (!UseUrvalButton) UseUrvalButton = UrvalButton;

  let CustomAddButton = undefined;
  if (props.collection.CollectionName === props.baseCollection.CollectionName)
    CustomAddButton = findRoutineMenuCustomAddButton(
      rutinSettingsContext.activeRoutine?.routine.rutinKod
    );

  const onVisningsButtonClick = useCallback(() => {
    setTabValue(0);
    onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCloseMiscMenu = useCallback(() => setMiscMenuAnchor(undefined), []);

  const isDisabled = rutinSettingsContext.hasRutinStartError;

  const renderFloatingButton = useCallback(
    (buttonProps: IRenderButtonProps) => (
      <FloatingMenuButton
        onClick={
          buttonProps.onClick as React.MouseEventHandler<HTMLButtonElement>
        }
        disabled={buttonProps.disabled}
        title={buttonProps.title}
      >
        {typeof buttonProps.icon === 'string' ? (
          <EDIcon iconType={buttonProps.icon} color="inherit" />
        ) : (
          buttonProps.icon
        )}
        <FloatingMenuButtonLabel>{buttonProps.label}</FloatingMenuButtonLabel>
      </FloatingMenuButton>
    ),
    []
  );

  return (
    <>
      <FloatingMenu>
        {!unusedButtons.includes('urval') &&
          (props.urval || props.setUrval) && (
            <UseUrvalButton
              urval={props.urval}
              setUrval={props.setUrval}
              collectionName={props.collection.CollectionName}
              refetch={props.refetch}
              renderButton={renderFloatingButton}
              routineID={rutinSettingsContext.activeRoutine?.routine.rutinID}
              errorDisable={isDisabled}
              errorText={isDisabled ? CollectionMenuDisabledReason : undefined}
              setSortInfoFromSelectionOrder={
                props.setSortInfoFromSelectionOrder
              }
            />
          )}
        {!unusedButtons.includes('column') && (
          <FloatingMenuButton
            onClick={onVisningsButtonClick}
            disabled={isDisabled}
            title={isDisabled ? CollectionMenuDisabledReason : undefined}
          >
            <EDIcon iconType="view_column" color="inherit" />
            <FloatingMenuButtonLabel>Visning</FloatingMenuButtonLabel>
          </FloatingMenuButton>
        )}
        {!unusedButtons.includes('add') && (
          <>
            {CustomAddButton != null ? (
              <CustomAddButton
                setNewObject={props.setNewObject}
                smallMenu
                renderButton={renderFloatingButton}
              />
            ) : props.setNewObject ? (
              <FloatingMenuButton
                onClick={props.setNewObject}
                disabled={props.errorDisable}
                title={
                  props.errorDisable ? CollectionMenuDisabledReason : undefined
                }
              >
                <EDIcon iconType="add" color="inherit" />
                <FloatingMenuButtonLabel>Lägg till</FloatingMenuButtonLabel>
              </FloatingMenuButton>
            ) : (
              <AddNewObjectButton
                collection={props.collection}
                onSave={handleRefetch}
                columns={props.columns}
                urval={props.urval || emptyUrval}
                baseCollection={props.baseCollection}
                baseId={props.baseId}
                renderButton={renderFloatingButton}
                hasRutinStartError={isDisabled}
              />
            )}
          </>
        )}
        <FloatingMenuButton
          onClick={setMenuAnchor}
          disabled={isDisabled}
          title={isDisabled ? CollectionMenuDisabledReason : undefined}
          data-testid="button-viewSelectMobile"
        >
          <EDIcon iconType="more_vert" color="inherit" />
          <FloatingMenuButtonLabel>Mer</FloatingMenuButtonLabel>
        </FloatingMenuButton>
      </FloatingMenu>
      <EDMenu
        id="misc-menu"
        anchorEl={miscMenuAnchor}
        open={Boolean(miscMenuAnchor)}
        onClose={onCloseMiscMenu}
      >
        {!unusedButtons.includes('refresh') && (
          <EDMenuItem color="inherit" onClick={handleRefetch}>
            <EDIcon iconType="refresh" />
            <EDTypography variant="subtitle1" sx={menuTextStyle}>
              Uppdatera
            </EDTypography>
          </EDMenuItem>
        )}
        {!unusedButtons.includes('excel') && (
          <EDMenuItem
            color="inherit"
            onClick={() => {
              exportToExcel();
              setMiscMenuAnchor(undefined);
            }}
            disabled={props.datasetLength < 1}
            disabledReason={
              props.datasetLength < 1 ? 'Inget att exportera' : undefined
            }
          >
            <EDIcon iconType="excel_export" />
            <EDTypography variant="subtitle1" sx={menuTextStyle}>
              Exportera till Excel
            </EDTypography>
          </EDMenuItem>
        )}
        {props.customBusinessCollectionMenuItems?.map(customItem => (
          <EDMenuItem
            color="inherit"
            onClick={() => {
              customItem.onClick?.();
              onCloseMiscMenu();
            }}
            key={customItem.id}
            selected={customItem.selected}
            testId={customItem.id}
          >
            {customItem.iconType && <EDIcon iconType={customItem.iconType} />}
            <EDTypography
              variant="subtitle1"
              sx={customItem.selected ? selectedMenuTextStyle : menuTextStyle}
            >
              {customItem.caption}
            </EDTypography>
          </EDMenuItem>
        ))}
        {businessColors.length > 0 && (
          <LegendMenuButton
            posts={businessColors}
            margin="none"
            inMobileMenu
            onClose={onCloseMiscMenu}
          />
        )}
      </EDMenu>
      {props.setColumns && (
        <KolumnValjareDialog
          valdaKolumner={props.columns}
          collectionName={props.collection.CollectionName}
          setValdaKolumner={props.setColumns}
          sortedData={props.sortedData}
          updateSortOrder={props.updateSortOrder}
          tabValue={tabValue}
          setTabValue={setTabValue}
          open={open}
          onClose={onClose}
          inAssociation={
            props.baseCollection.CollectionName !==
            props.collection.CollectionName
          }
        />
      )}
    </>
  );
};

export default memo(FloatingBusinessCollectionMenu);
